import { Handle, Position } from '@xyflow/react';
import type { HandleProps } from '@xyflow/react';
import { useState } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import { Menu } from '@mantine/core';
import { IconPhoneOff, IconHash, IconPlug, IconClockOff } from '@tabler/icons-react';
import { useHandleAction } from '../handle-action-context';
import type { HandleActionType, HandlePosition } from '../../../widgets/types';
import { actionLabels } from '../../../widgets/action-labels';
import { allowedActionTypes, connectActionLabel } from '../../../lib/flow-editor/action-options';

type LabeledHandleProps = Omit<HandleProps, 'position' | 'isConnectable'> & {
  label: string;
  position: HandlePosition;
  offset?: string;
  nodeId?: string;
};

const handlePositions: Record<HandlePosition, Position> = {
  top: Position.Top,
  bottom: Position.Bottom,
  left: Position.Left,
  right: Position.Right,
};

const labelPlacements: Record<HandlePosition, CSSProperties> = {
  top: { top: -22, transform: 'translateX(-50%)' },
  bottom: { bottom: -22, transform: 'translateX(-50%)' },
  left: { left: -8, transform: 'translate(-100%, -50%)' },
  right: { right: -8, transform: 'translate(100%, -50%)' },
};

function offsetStyle(position: HandlePosition, offset: string | undefined): CSSProperties {
  return position === 'top' || position === 'bottom' ? { left: offset } : { top: offset };
}

const actionIcons: Record<HandleActionType, typeof IconPhoneOff> = {
  hangup: IconPhoneOff,
  zero: IconHash,
  officeClosed: IconClockOff,
};

function ActionIcon({ type, size }: { type: HandleActionType; size: number }) {
  const Icon = actionIcons[type];
  return <Icon size={size} />;
}

export function LabeledHandle({ label, position, offset, nodeId, ...handleProps }: LabeledHandleProps) {
  const { getHandleAction, setHandleAction, isZeroRouteAllowed, isOfficeClosedAllowed } = useHandleAction();
  const [menuOpened, setMenuOpened] = useState(false);

  const isSource = handleProps.type === 'source';
  const action = nodeId && isSource ? getHandleAction(nodeId, handleProps.id ?? '') : undefined;
  const hasContextMenu = isSource && nodeId;

  const handleContextMenu = (e: MouseEvent) => {
    if (!hasContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    setMenuOpened(true);
  };

  const labelStyle: CSSProperties = {
    position: 'absolute',
    fontSize: 9,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    padding: '2px 4px',
    borderRadius: 3,
    background: action ? 'var(--mantine-color-red-light)' : 'var(--mantine-color-default-hover)',
    color: action ? 'var(--mantine-color-red-text)' : 'var(--mantine-color-dimmed)',
    pointerEvents: 'none',
    ...offsetStyle(position, offset),
    ...labelPlacements[position],
  };

  const handleDotStyle: CSSProperties = {
    ...offsetStyle(position, offset),
    ...(action
      ? {
          background: 'var(--mantine-color-red-filled)',
          borderColor: 'var(--mantine-color-red-filled)',
        }
      : {}),
  };

  const handleId = handleProps.id ?? '';

  const content = (
    <>
      <Handle position={handlePositions[position]} style={handleDotStyle} {...handleProps} />
      <span style={labelStyle}>
        {action && (
          <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 2 }}>
            <ActionIcon type={action.type} size={8} />
          </span>
        )}
        {label}
      </span>
    </>
  );

  if (!hasContextMenu) return content;

  return (
    <Menu opened={menuOpened} onChange={setMenuOpened} position="bottom-start" withArrow>
      <Menu.Target>
        <span style={{ display: 'contents' }} onContextMenu={handleContextMenu}>
          {content}
        </span>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Handle Action</Menu.Label>
        <Menu.Item
          leftSection={<IconPlug size={14} />}
          onClick={() => setHandleAction(nodeId, handleId, undefined)}
          disabled={!action}
        >
          {connectActionLabel}
        </Menu.Item>
        {allowedActionTypes(isZeroRouteAllowed, isOfficeClosedAllowed, nodeId, handleId).map((t) => (
          <Menu.Item
            key={t}
            leftSection={<ActionIcon type={t} size={14} />}
            onClick={() => setHandleAction(nodeId, handleId, { type: t })}
            disabled={action?.type === t}
          >
            {actionLabels[t]}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
